import _ from "underscore";

import { LOCATIONS_TAGS_CHARACTERS_LIMIT } from "@js/apps/jobs/apps/create-job/constants";
import { mapRateValuesToBackendFormat } from "@js/apps/jobs/apps/create-job/utils/rate-mapping";
import { mapTimezonesToBackendFormat } from "@js/apps/jobs/apps/create-job/utils/timezones-mapping";
import { sanitize } from "@js/services";
import type { Location } from "@js/types/common";
import type {
  CreateJobData,
  CreateJobDraftPayload,
  GetJobDraftResponse,
  JobDraft,
  JobFormValues,
  PreparedJobDraftData,
  TimezoneOption,
  UpdateJobData,
  UpdateJobDraftPayload,
} from "@js/types/jobs";
import { deepClone, typedObjectKeys } from "@js/utils";

const fieldsToSanitize = ["requirements", "introduction"] as const;
const sanitizationRegex = />[^<]*[^<>\s]+[^>]*</i;

export const sanitizeRichEditorContent = <
  T extends { requirements?: string; introduction?: string } | undefined,
>(
  formValues: T,
): T => {
  if (formValues) {
    fieldsToSanitize.forEach((editorField) => {
      const fieldValue = formValues[editorField];

      if (fieldValue !== undefined && !sanitizationRegex.test(fieldValue)) {
        formValues[editorField] = "";
      }
    });
  }

  return formValues;
};

export const getSanitizedQuestions = <TQuestion extends { question: string }>(
  questions: TQuestion[],
): TQuestion[] => {
  return questions.map((questionItem) => ({
    ...questionItem,
    question: sanitize(questionItem.question, { ALLOWED_TAGS: [] }),
  }));
};

const removeUndefinedKeys = <T extends Record<string, unknown>>(obj: T): T => {
  return typedObjectKeys(obj).reduce((prev, current) => {
    if (obj[current] !== undefined) {
      prev[current] = obj[current];
    }

    return prev;
  }, {} as T);
};

export const prepareDraftResponse = (
  response: GetJobDraftResponse,
): JobDraft => {
  const responseWithoutData: Omit<GetJobDraftResponse, "data"> & {
    data?: undefined;
  } = {
    ...response,
    data: undefined,
  };

  delete responseWithoutData.data;

  return {
    ...responseWithoutData,
    ...response.data,
  };
};
export const prepareJobDraftFormValues = (
  valuesArg: JobFormValues,
): PreparedJobDraftData => {
  const values: PreparedJobDraftData = deepClone({
    ...mapRateValuesToBackendFormat<JobFormValues>(valuesArg),
    sub_type: valuesArg.sub_type ?? undefined,
    talent_invoice_editing_disabled: valuesArg.talent_invoice_editing_disabled,
    skip_approval_to_job: valuesArg.skip_approval_to_job,
    employer_skip_approval_to_job: undefined,
    automatic_linkedin_posting_enabled:
      valuesArg.automatic_linkedin_posting_enabled,
    automatic_vonq_posting_enabled: valuesArg.automatic_vonq_posting_enabled,
    payment_conditions: valuesArg.payment_conditions,
    jobOwnerId: undefined,
    isDeletingJobDraft: undefined,
    ats_imported: undefined,
    timezones: valuesArg.timezones?.length
      ? mapTimezonesToBackendFormat(valuesArg.timezones)
      : [],
    locations: valuesArg.locations?.length ? valuesArg.locations : [],
  });

  if (!values.timezones.length) {
    values.timezone_overlap = null;
  }

  if (!values.locations.length) {
    values.locations_strongly_required = false;
  }

  if (!values.is_ai_interview_enabled) {
    delete values.ai_interview_maximum_attempts;
    delete values.ai_interview_max_duration_in_minutes;
    delete values.interview_criteria;
    delete values.interview_questions;
    delete values.extension?.intro_video;
  } else {
    values.extension = {
      intro_video: valuesArg.intro_video?.id ?? null,
    };
  }

  values.op_owners =
    !!values.op_owners && values.op_owners.length > 0
      ? values.op_owners.map((owner) => owner)
      : [];
  values.sale_owners =
    !!values.sale_owners && values.sale_owners.length > 0
      ? values.sale_owners.map((owner) => owner)
      : [];

  values.other_role_name =
    values.role === SETTINGS.OTHER_ROLE_ID ? values.other_role_name : undefined;

  return values;
};

const normalizeJobDraftValuesForComparison = <
  T extends Pick<PreparedJobDraftData, "auto_invite_talent">,
>(
  values: T,
): T => {
  return { ...values, auto_invite_talent: undefined };
};

export const prepareLatestDraftDataToSave = (
  values: JobFormValues,
  lastSavedDraft: JobDraft | undefined,
): PreparedJobDraftData | undefined => {
  const valuesCopy = deepClone(values);

  const filteredValues = removeUndefinedKeys(
    prepareJobDraftFormValues(valuesCopy),
  );
  const draftDataToSave = sanitizeRichEditorContent(filteredValues);
  const toCompare = sanitizeRichEditorContent(
    _.pick(lastSavedDraft, Object.keys(filteredValues)) ?? {},
  );

  // If you wonder why there is additional request after creating a new draft:
  // It is because we initialize form with values depending on job_type (e.g. contract_type).
  // To avoid request we would need to submit those values on draft set up.
  // Form values are sanitized to avoid unnecessary requests when rich editor field doesn't contain content
  // but editor itself fills value with HTML tags (f.e. only white spaces or enters)

  const areEqual = _.isEqual(
    normalizeJobDraftValuesForComparison(draftDataToSave),
    normalizeJobDraftValuesForComparison(toCompare),
  );

  if (areEqual) {
    return;
  }

  return draftDataToSave;
};

const withoutId = <T extends { id: unknown }>(arg: T): Omit<T, "id"> => {
  const argWithoutId: Omit<T, "id"> & { id?: unknown } = { ...arg };
  delete argWithoutId.id;

  return argWithoutId;
};

export const prepareCreateJobSubmitFormValues = (
  valuesArg: JobFormValues,
): CreateJobData | undefined => {
  const jobDraftId = valuesArg.id;
  if (!jobDraftId) {
    return undefined;
  }
  const preparedDraftValues = prepareJobDraftFormValues(valuesArg);
  const preparedDraftValuesWithoutId = withoutId(preparedDraftValues);

  const paymentConditions = preparedDraftValuesWithoutId.payment_conditions;

  return {
    ...preparedDraftValuesWithoutId,
    job_draft_id: jobDraftId,
    client_pays_talent_directly: paymentConditions === "client_pays_directly",
    braintrust_pays_talent_off_platform:
      paymentConditions === "braintrust_pays_off_platform",
  };
};

export const prepareEditJobSubmitFormValues = ({
  valuesArg,
  isEmployer,
  isNodeStaffOrImpersonated,
}: {
  valuesArg: JobFormValues;
  isEmployer?: boolean;
  isNodeStaffOrImpersonated?: boolean;
}): UpdateJobData => {
  const preparedValues = prepareJobDraftFormValues(valuesArg);

  const updateJobData = {
    ..._.pick(preparedValues, [
      "title",
      "contract_type",
      "deadline",
      "start_date",
      "expected_hours_per_week",
      "openings_number",
      "role",
      "introduction",
      "description",
      "requirements",
      "budget_maximum_usd",
      "budget_minimum_usd",
      "level",
      "payment_type",
      "new_application_questions",
      "auto_invite_talent",
      "locations",
      "locations_strongly_required",
      "timezones",
      "timezone_overlap",
      "new_skills",
      "top_skills",
      "is_private",
      "experience_level",
      "is_resume_required",
      "job_subscribers",
      "is_ai_interview_enabled",
      "ai_interview_max_duration_in_minutes",
      "ai_interview_maximum_attempts",
      "interview_criteria",
      "interview_questions",
      "other_role_name",
      "extension",
      "talent_invoice_editing_disabled",
      ...(isEmployer ? ["job_subscriber_invitations"] : []),
      ...(isNodeStaffOrImpersonated
        ? [
            "job_type",
            "automatic_linkedin_posting_enabled",
            "automatic_vonq_posting_enabled",
            "skip_approval_to_job",
          ]
        : []),
    ]),
  } as UpdateJobData;

  if (!isNodeStaffOrImpersonated) {
    return updateJobData;
  }

  const paymentConditions = valuesArg.payment_conditions;
  return {
    ...updateJobData,
    sub_type: preparedValues.sub_type ?? null,
    client_pays_talent_directly: paymentConditions === "client_pays_directly",
    braintrust_pays_talent_off_platform:
      paymentConditions === "braintrust_pays_off_platform",
  };
};

export type PrepareArrayWithTagsCharactersLimitEntity =
  TimezoneOption | Location;

export const prepareArrayWithTagsCharactersLimit = <
  T extends PrepareArrayWithTagsCharactersLimitEntity,
>(
  entities: T[],
  tagsCharactersLimit: number,
  getName: (item: NoInfer<T>) => string,
  focused: boolean,
): T[] => {
  if (focused) return entities;

  let lengthCounter = 0;

  return entities.reduce((accumulator: T[], currentItem: T) => {
    const itemName = getName(currentItem);

    const hasExceededCharacterLimit =
      lengthCounter + itemName.length >= tagsCharactersLimit;

    lengthCounter += itemName.length;

    if (hasExceededCharacterLimit && accumulator.length > 0) {
      return accumulator;
    }

    return [...accumulator, currentItem];
  }, []);
};

export const prepareAutocompleteValue = ({
  value,
  isLast,
  isInputFocused,
  isLimitReached,
  limit = LOCATIONS_TAGS_CHARACTERS_LIMIT,
}: {
  value: string;
  isLast: boolean;
  isInputFocused: boolean;
  isLimitReached: boolean;
  limit?: number;
}) => {
  let ellipsis = "";
  let processedValue = value;

  if (!isLast || isInputFocused) {
    ellipsis = ` |`;
  } else if (isLimitReached) {
    ellipsis = ` | ...`;
  }

  if (processedValue.length > limit && !isInputFocused) {
    processedValue = processedValue.slice(0, limit);
    ellipsis = "...";
  }

  return (
    <span
      key={value}
      style={{
        marginRight: "5px",
      }}
      data-testid="job-location-display-value"
    >
      {`${processedValue}${ellipsis}`}
    </span>
  );
};

type SaveDraftArg = {
  data: Optional<PreparedJobDraftData, "id">;
  original_job?: number | null;
};

type SaveDraftPayload = UpdateJobDraftPayload | CreateJobDraftPayload;

export const prepareJobDraftPayload = (
  draftDataToSave: SaveDraftArg,
): SaveDraftPayload => {
  const draftDataToSaveClone = deepClone(draftDataToSave);
  const preparedJobDraftPayloadData: SaveDraftPayload["data"] & {
    job_subscribers?: undefined;
    job_subscriber_invitations?: undefined;
  } = {
    ...draftDataToSaveClone.data,
    job_subscribers: undefined,
    job_subscriber_invitations: undefined,
  };

  delete preparedJobDraftPayloadData.job_subscribers;
  delete preparedJobDraftPayloadData.job_subscriber_invitations;

  return {
    ...draftDataToSaveClone,
    data: preparedJobDraftPayloadData,
    job_subscribers: draftDataToSaveClone.data.job_subscribers || [],
    job_subscriber_invitations:
      draftDataToSaveClone.data.job_subscriber_invitations || [],
  };
};
